import { toast } from "sonner";
import { getErrorMessage, getValidationErrors } from "./errorHandler";

export interface NotifyErrorOptions {
  /** Toast headline. Defaults to the resolved error message. */
  title?: string;
  /** Message used when the error carries no usable message of its own. */
  fallbackMessage?: string;
  /** Include per-field validation messages in the toast description. Default true. */
  includeFieldErrors?: boolean;
}

/**
 * Single entry point for surfacing an error to the user as a toast.
 *
 * Prefers the backend's human-readable message (via {@link getErrorMessage}) and,
 * for validation errors, lists the offending fields in the toast description.
 * Used by the global MutationCache.onError handler and anywhere a component needs
 * to report an error consistently.
 */
export function notifyError(error: unknown, opts: NotifyErrorOptions = {}): void {
  const message = getErrorMessage(error) || opts.fallbackMessage || "Something went wrong";
  const fieldErrors = opts.includeFieldErrors === false ? null : getValidationErrors(error);

  // When we show a separate title, the message becomes the description; otherwise
  // surface any field-level validation messages as the description.
  const description = fieldErrors
    ? Object.values(fieldErrors).flat().join("\n")
    : opts.title
      ? message
      : undefined;

  toast.error(opts.title ?? message, description ? { description } : undefined);
}

/** Success toast counterpart, for parity with {@link notifyError}. */
export function notifySuccess(message: string, description?: string): void {
  toast.success(message, description ? { description } : undefined);
}
